import { useEffect, useState, type ComponentType, type ReactNode } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@roger/ui-components';

import { MOTION } from './chatMotion';
import { loadEmojiData } from './emojiShortcodes';

interface EmojiPickerProps {
  trigger: ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (native: string) => void;
  /** Phone sheet sizing: 8 per row, 40px buttons, no autofocus. */
  touch?: boolean;
  onCloseAutoFocus?: (e: Event) => void;
}

// Popover edge padding (collisionPadding) on both sides, and the picker's own
// padding and sidebar, which emoji-mart adds to the buttons' width.
const EDGE_PADDING = 16;
const PICKER_CHROME = 28;

/** Columns that fit the viewport, so a 320px phone does not overflow. */
function columnsThatFit(max: number, buttonSize: number): number {
  const fit = Math.floor(
    (window.innerWidth - EDGE_PADDING - PICKER_CHROME) / buttonSize
  );
  return Math.max(1, Math.min(max, fit));
}

/** The emoji-mart picker in a popover. The picker code loads on first open. */
export function EmojiPicker({
  trigger,
  open,
  onOpenChange,
  onSelect,
  touch = false,
  onCloseAutoFocus,
}: EmojiPickerProps) {
  const [Picker, setPicker] = useState<ComponentType<
    Record<string, unknown>
  > | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open || Picker || failed) return;
    let cancelled = false;
    Promise.all([loadEmojiData(), import('@emoji-mart/react')]).then(
      ([, mod]) => {
        // A function value must go through an updater, or React calls it.
        if (!cancelled) setPicker(() => mod.default);
      },
      error => {
        console.error('Emoji picker failed to load', error);
        if (!cancelled) setFailed(true);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [open, Picker, failed]);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        className={`w-auto p-0 ${MOTION.reduceRadix}`}
        collisionPadding={8}
        side='top'
        align='start'
        onCloseAutoFocus={onCloseAutoFocus}
      >
        {failed ? (
          <p className='p-3 text-sm text-muted-foreground'>
            Emoji could not load
          </p>
        ) : Picker ? (
          <Picker
            previewPosition='none'
            skinTonePosition='search'
            theme='light'
            perLine={touch ? columnsThatFit(8, 40) : columnsThatFit(9, 36)}
            {...(touch ? { emojiButtonSize: 40 } : {})}
            autoFocus={!touch}
            onEmojiSelect={(e: { native: string }) => onSelect(e.native)}
          />
        ) : (
          <div className='h-16 w-40' />
        )}
      </PopoverContent>
    </Popover>
  );
}
